import React, { useEffect } from 'react';
import { X, TrendingUp, DollarSign, Target, PieChart, Activity, Calendar, CreditCard, Brain, Bitcoin, Home, BarChart3, Zap, Shield, Globe, Download, Lock } from 'lucide-react';
import { updatePageSEO } from '../utils/seo';

const FeaturesPage = ({ onClose }) => {
  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'Features - PlanLens.ai | Complete Financial Planning Toolkit',
      description: 'Explore PlanLens.ai features: Portfolio tracking, retirement planning, AI-powered insights, cryptocurrency management, net worth dashboard, Monte Carlo simulations, and more. Privacy-focused wealth management tools.',
      canonical: 'https://planlens.ai/features'
    });
  }, []);

  const features = [
    {
      category: "Wealth Management",
      icon: TrendingUp,
      color: "blue",
      items: [
        {
          name: "Net Worth Dashboard",
          description: "Get a complete 360° view of your financial health with real-time net worth calculations combining all your assets and liabilities.",
          features: ["Real-time calculations", "Financial health score (0-100)", "Debt-to-asset ratio tracking", "Visual breakdowns with charts"]
        },
        {
          name: "Portfolio Tracking",
          description: "Track all your investments across multiple accounts with automatic portfolio allocation calculations.",
          features: ["Multi-account support", "Real-time valuations", "Asset allocation charts", "Performance tracking"]
        }
      ]
    },
    {
      category: "Financial Planning",
      icon: Target,
      color: "green",
      items: [
        {
          name: "Financial Goals",
          description: "Set and track progress toward your financial goals with intelligent projections and recommendations.",
          features: ["11 goal types (Home, Education, Retirement, etc.)", "Progress tracking with visual indicators", "Required monthly savings calculator", "Future value projections with compound interest", "Goal status tracking (On Track, At Risk, Urgent)", "Priority-based sorting"]
        },
        {
          name: "Liabilities Management",
          description: "Track and manage all your debts including mortgages, loans, and credit cards in one place.",
          features: ["6 liability types supported", "Interest rate tracking", "Monthly payment tracking", "Payoff progress visualization", "Total debt calculations"]
        },
        {
          name: "Enhanced Retirement Planning",
          description: "Advanced retirement planning with interactive calculators, scenario analysis, and AI-powered insights to help you achieve your retirement goals.",
          features: ["Interactive retirement calculator with real-time projections", "Multiple scenario analysis (optimistic, expected, pessimistic)", "Contribution and withdrawal strategy optimization", "Social Security/State Pension integration", "Inflation-adjusted projections", "Visual timeline charts and graphs", "AI-powered retirement recommendations"]
        }
      ]
    },
    {
      category: "Account Management",
      icon: DollarSign,
      color: "purple",
      items: [
        {
          name: "US & UK Account Types",
          description: "Manage country-specific investment accounts with proper tax treatment and contribution limits.",
          features: ["US: 401(k), IRA, Roth IRA, Taxable", "UK: ISA, SIPP, Personal Pension, General Investment", "Automatic contribution limit tracking", "Tax-advantaged account optimization"]
        },
        {
          name: "Multi-Account Portfolio",
          description: "View your entire portfolio across all accounts or drill down into individual account performance.",
          features: ["Consolidated portfolio view", "Per-account breakdowns", "Account balance tracking", "Asset allocation by account"]
        }
      ]
    },
    {
      category: "Analytics & Insights",
      icon: Brain,
      color: "yellow",
      items: [
        {
          name: "Gemini AI-Powered Analysis",
          description: "Get intelligent insights and personalized recommendations powered by Google's Gemini AI with advanced natural language understanding.",
          features: ["Portfolio risk assessment with AI insights", "Diversification scoring and recommendations", "Natural language analysis and explanations", "Country-specific optimization strategies", "Personalized action items tailored to your goals", "Context-aware financial insights", "Comprehensive portfolio health reports"]
        },
        {
          name: "Portfolio Analysis",
          description: "Advanced portfolio analysis tools including historical backtesting and Monte Carlo simulations.",
          features: ["Historical backtesting (1995-2024)", "Monte Carlo simulations (10,000 scenarios)", "Risk-adjusted returns (Sharpe Ratio)", "Best/worst/expected case scenarios", "Customizable allocations"]
        },
        {
          name: "Retirement Drawdown Simulator",
          description: "Test different withdrawal strategies to ensure your retirement savings last.",
          features: ["Multiple withdrawal scenarios", "Success rate calculations", "Inflation-adjusted projections", "Visual timeline charts"]
        }
      ]
    },
    {
      category: "International Support",
      icon: Globe,
      color: "indigo",
      items: [
        {
          name: "Multi-Country Support",
          description: "Seamlessly switch between US and UK markets with automatic currency and account type adjustments.",
          features: ["US Dollar ($) and British Pound (£)", "Country-specific account types", "Localized tax rules", "Market-specific recommendations"]
        },
        {
          name: "Currency Conversion",
          description: "All calculations and displays automatically adjust to your selected country's currency.",
          features: ["Automatic currency formatting", "Real-time conversions", "Consistent display throughout app"]
        }
      ]
    },
    {
      category: "Data & Security",
      icon: Shield,
      color: "red",
      items: [
        {
          name: "Secure Cloud Storage",
          description: "Your financial data is securely stored in Firebase with industry-standard encryption.",
          features: ["Bank-level encryption", "Secure authentication", "Privacy-first design", "GDPR compliant"]
        },
        {
          name: "Real-Time Sync",
          description: "All your data syncs instantly across devices so you can access it anywhere.",
          features: ["Automatic cloud backup", "Cross-device sync", "No data loss", "Always up-to-date"]
        },
        {
          name: "CSV Import/Export",
          description: "Import existing investment data or export your portfolio for external analysis.",
          features: ["Bulk CSV import", "Template generation", "Data validation", "Easy migration"]
        },
        {
          name: "Advanced Exports",
          description: "Export your data and reports in multiple formats for record-keeping and analysis.",
          features: ["CSV export for investments", "PDF report generation", "Portfolio snapshots", "Historical data downloads"]
        }
      ]
    },
    {
      category: "User Experience",
      icon: Zap,
      color: "orange",
      items: [
        {
          name: "Dark Mode",
          description: "Easy on the eyes with a beautiful dark mode that works throughout the entire app.",
          features: ["System-wide dark theme", "Automatic persistence", "Reduced eye strain", "Modern aesthetics"]
        },
        {
          name: "Mobile Responsive",
          description: "Access your financial data on any device - desktop, tablet, or mobile phone.",
          features: ["Fully responsive design", "Touch-optimized interface", "Works on all screen sizes", "Native app-like experience"]
        },
        {
          name: "Help & Learning Center",
          description: "Comprehensive educational resources to help you make informed financial decisions.",
          features: ["Investment education", "Feature tutorials", "FAQ section", "Glossary of terms"]
        }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: "bg-blue-100",
        iconText: "text-blue-600"
      },
      green: {
        bg: "bg-green-50",
        border: "border-green-200",
        icon: "bg-green-100",
        iconText: "text-green-600"
      },
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        icon: "bg-purple-100",
        iconText: "text-purple-600"
      },
      indigo: {
        bg: "bg-indigo-50",
        border: "border-indigo-200",
        icon: "bg-indigo-100",
        iconText: "text-indigo-600"
      },
      red: {
        bg: "bg-red-50",
        border: "border-red-200",
        icon: "bg-red-100",
        iconText: "text-red-600"
      },
      orange: {
        bg: "bg-orange-50",
        border: "border-orange-200",
        icon: "bg-orange-100",
        iconText: "text-orange-600"
      }
    };

    return colors[color] || colors.blue;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center rounded-t-2xl z-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Complete Feature List</h2>
            <p className="text-gray-600 mt-1">Everything PlanLens has to offer</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {features.map((category, categoryIndex) => {
            const Icon = category.icon;
            const colorClasses = getColorClasses(category.color);

            return (
              <div key={categoryIndex}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-xl ${colorClasses.icon}`}>
                    <Icon className={`w-6 h-6 ${colorClasses.iconText}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                  </div>
                </div>

                {/* Features in Category */}
                <div className="space-y-4">
                  {category.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className={`p-6 rounded-xl border-2 ${colorClasses.bg} ${colorClasses.border}`}
                    >
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-gray-700 mb-4">{item.description}</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {item.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Feature Count Summary */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white text-center">
            <h3 className="text-2xl font-bold mb-2">50+ Features & Growing</h3>
            <p className="text-blue-100">
              We're constantly adding new features to help you achieve your financial goals.
              Join thousands of users already planning smarter with PlanLens!
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="text-center sm:text-left">
              <p className="text-sm text-gray-600">Ready to get started?</p>
              <p className="text-xs text-gray-500">Free account includes most features • 14-day premium trial</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;
